import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, CalendarDays, Clock } from "lucide-react";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* USA INDEPENDENCE DAY 2026 */}
      <section className="relative overflow-hidden border-b border-[color:var(--line)]/20" data-testid="usa-day-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 pt-14 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-in">
            <div className="eyebrow" data-testid="usa-day-eyebrow">4 Iulie 2026 · Ziua Independenței</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-[color:var(--ink)]" data-testid="usa-day-title">
              La mulți ani,<br />
              <span className="font-display-italic text-[color:var(--gold)]">Statelor Unite ale Americii!</span>
            </h2>
            <p className="mt-6 max-w-xl text-[color:var(--ink-soft)] text-base leading-relaxed">
              La 250 de ani de la Declarația de Independență, Asociația General George Pomuț
              transmite un cald omagiu poporului american — pe care un român, prin curajul și
              diplomația sa, a ajutat să-l făurească.
            </p>
            <div className="mt-8 hidden sm:flex items-center gap-4" data-testid="usa-day-flags">
              <img src="/assets/flag-us-250.png" alt="Steagul SUA 250 de ani" className="w-32 sm:w-40 h-auto object-contain border border-[color:var(--ink)]/15 shadow-[0_10px_25px_-15px_rgba(15,27,45,0.4)]" />
              <img src="/assets/flag-romania.png" alt="Drapelul României" className="w-32 sm:w-40 h-auto object-contain border border-[color:var(--ink)]/15 shadow-[0_10px_25px_-15px_rgba(15,27,45,0.4)]" />
            </div>
          </div>

          <div className="lg:col-span-5 fade-in delay-2" data-testid="usa-day-sculpture">
            <figure className="relative">
              <div className="absolute -top-3 -left-3 w-20 h-20 border border-[color:var(--gold)]/40" />
              <div className="absolute -bottom-3 -right-3 w-20 h-20 border border-[color:var(--ink)]/25" />
              <div className="relative bg-[color:var(--cream)] p-3">
                <img
                  src="/assets/sculptura-pomut.jpg"
                  alt="Bustul generalului George Pomuț pe fundalul drapelelor SUA și României"
                  className="w-full h-auto object-contain"
                  data-testid="sculpture-image"
                />
              </div>
              <figcaption className="mt-4 flex items-baseline justify-between px-1">
                <span className="eyebrow">Sculptor</span>
                <span className="font-display-italic text-[15px] text-[color:var(--ink)]" data-testid="sculpture-credit">
                  Răzvan Paul Mihăescu
                </span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-in">
            <div className="eyebrow" data-testid="hero-eyebrow">Asociația · Est. în memoria 1818–1882</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6 text-[color:var(--ink)]" data-testid="hero-title">
              Omagierea diplomatului<br />
              <span className="font-display-italic text-[color:var(--gold)]">general George Pomuț</span><br />
              la Oradea.
            </h1>
            <p className="mt-8 max-w-xl text-[color:var(--ink-soft)] text-base leading-relaxed" data-testid="hero-subtitle">
              250 de ani de la Declarația de Independență a Statelor Unite ale Americii.
              Un demers cultural pentru recuperarea memoriei unui român care a luptat
              pentru idealurile de libertate și a contribuit la edificarea Americii.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4" data-testid="hero-ctas">
              <Link to="/eveniment" className="btn-primary" data-testid="hero-cta-event">
                Detalii Simpozion <ArrowUpRight size={16} />
              </Link>
              <a href="#biografie" className="btn-ghost" data-testid="hero-cta-bio">
                Despre General
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 fade-in delay-2" data-testid="hero-portrait-card">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border border-[color:var(--gold)]/40" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-[color:var(--ink)]/30" />
              <div className="relative bg-[color:var(--cream)] p-3">
                <img
                  src="/assets/general-pomut-portrait.jpg"
                  alt="Portret General George Pomuț, secolul al XIX-lea"
                  className="w-full h-[480px] object-contain bg-[#0F1B2D]"
                  data-testid="hero-portrait-image"
                />
                <div className="pt-3 pb-1 px-1 flex items-baseline justify-between">
                  <span className="font-display text-lg">Gheorghe Pomuț</span>
                  <span className="eyebrow text-[10px]">1818 — 1882</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="rule mx-auto max-w-[1280px]" />
      </section>

      {/* VIDEO SIMPOZION */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pt-16 pb-8" data-testid="video-section">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="eyebrow">Istoria · Prezentare</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-[1.05]">
              Cine este George Pomuț:<br />
              <span className="font-display-italic text-[color:var(--gold)]">Al doilea erou al lumii.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[color:var(--ink-soft)] text-sm leading-relaxed">
            Istoria și prezentarea generalului George Pomuț — un român care a luptat
            pentru idealurile de libertate, ofițer al Uniunii și diplomat al
            Statelor Unite ale Americii.
          </div>
        </div>

        <div className="relative gold-border bg-[color:var(--ink)]" data-testid="video-frame">
          <video
            className="w-full h-auto block aspect-video"
            controls
            preload="metadata"
            playsInline
            poster="/media/simpozion-poster.jpg"
            data-testid="simpozion-video"
          >
            <source src="/media/simpozion-preview.mp4" type="video/mp4" />
            Browserul dvs. nu suportă redarea video. Vă rugăm să descărcați fișierul.
          </video>
        </div>
      </section>

      {/* ANUNT EVENIMENT */}
      <section className="ink-bg" data-testid="event-banner">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow text-[color:var(--gold-soft)]">Eveniment apropiat</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[color:var(--ivory)]">
              Simpozion Omagial<br />
              <span className="font-display-italic text-[color:var(--gold-soft)]">General George Pomuț</span>
            </h2>
          </div>
          <div className="md:col-span-5 space-y-4 text-[color:var(--cream)]">
            <div className="flex items-start gap-4" data-testid="event-banner-date">
              <CalendarDays className="text-[color:var(--gold-soft)] mt-1" size={20} />
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--cream)]/60">Data</div>
                <div className="font-display text-2xl">30 Mai</div>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="event-banner-time">
              <Clock className="text-[color:var(--gold-soft)] mt-1" size={20} />
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--cream)]/60">Ora</div>
                <div className="font-display text-2xl">11:00 — 13:00</div>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="event-banner-location">
              <MapPin className="text-[color:var(--gold-soft)] mt-1" size={20} />
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--cream)]/60">Locație</div>
                <div className="font-display text-lg leading-snug">Biblioteca Județeană „Gheorghe Șincai” · Oradea</div>
              </div>
            </div>
            <Link to="/eveniment" className="inline-flex items-center gap-2 text-[color:var(--gold-soft)] hover:text-[color:var(--ivory)] mt-3 uppercase tracking-[0.22em] text-xs" data-testid="event-banner-cta">
              Vezi programul complet <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* DESPRE ASOCIATIE */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-16" data-testid="about-section">
        <div className="lg:col-span-4">
          <div className="eyebrow">I. Asociația</div>
          <h2 className="font-display text-4xl mt-4 leading-tight">Un demers de recuperare a memoriei.</h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 text-[color:var(--ink-soft)] text-[17px] leading-[1.75]">
          <p className="drop-cap">
            Asociația General George Pomuț reunește istorici, cercetători și cetățeni
            din Oradea și din Bihor, alături de partenerii noștri culturali — Biblioteca
            Județeană „Gheorghe Șincai”, Uniunea Culturală a Românilor din Ungaria și
            revista <em>Foaia Românească</em> — pentru a aduce în atenția publicului
            personalitatea remarcabilă a generalului și diplomatului George Pomuț.
          </p>
          <p className="mt-6">
            Inițiativa noastră include un simpozion anual, demersuri pentru atribuirea
            numelui generalului unei străzi din Oradea, identificarea unei piațete
            publice și ridicarea unui bust care să cinstească memoria celui pe care
            Walter O. Gresham îl numea „cel mai brav general” al Războiului Civil American.
          </p>
        </div>
      </section>

      <div className="rule max-w-[1280px] mx-auto" />

      {/* BIOGRAFIE */}
      <section id="biografie" className="max-w-[1280px] mx-auto px-6 lg:px-12 py-24" data-testid="biography-section">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">II. Biografie</div>
            <h2 className="font-display text-5xl mt-4 leading-[1.05]">
              Omul care a<br />
              <span className="font-display-italic text-[color:var(--gold)]">cumpărat Alaska.</span>
            </h2>
            <p className="mt-6 text-[color:var(--ink-soft)] leading-relaxed">
              Născut la 31 mai 1818 la Gyula, în Partium, educat la Viena și Saint-Étienne,
              revoluționar al anilor 1848–1849, refugiat politic în Statele Unite,
              general decorat al Uniunii și consul al SUA la Sankt Petersburg.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {[
              { year: "1818", title: "Naștere", text: "Se naște la 31 mai în Gyula, Partium — astăzi la granița româno-maghiară." },
              { year: "1848", title: "Revoluționar", text: "Participă activ la Revoluția maghiară de la 1848–1849 pentru idealurile de libertate." },
              { year: "1861–65", title: "Războiul Civil", text: "Ofițer al Uniunii în Iowa Volunteer Infantry, decorat și avansat general." },
              { year: "1867", title: "Achiziția Alaska", text: "Contribuie decisiv, în calitate de diplomat, la cumpărarea Alaskăi de la Rusia." },
              { year: "1874", title: "Consul general", text: "Numit Consul General al SUA la Sankt Petersburg, Imperiul Rus." },
              { year: "1882", title: "Trecere la cele veșnice", text: "Se stinge la 12 octombrie. Recunoscut postum de Congresul SUA în 1913." },
            ].map((m, i) => (
              <div key={m.year} className={`gold-border bg-[color:var(--cream)]/60 p-6 fade-in delay-${(i % 3) + 1}`} data-testid={`bio-card-${m.year}`}>
                <div className="font-display text-3xl text-[color:var(--gold)]">{m.year}</div>
                <div className="font-display text-xl mt-1">{m.title}</div>
                <p className="text-sm text-[color:var(--ink-soft)] mt-3 leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITAT */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pb-24" data-testid="quote-section">
        <figure className="gold-border bg-[color:var(--cream)]/40 p-10 md:p-16">
          <blockquote className="font-display-italic text-3xl md:text-4xl leading-snug text-[color:var(--ink)]">
            „Un român care, în anii tinereții, a luptat pentru idealurile de libertate —
            unul dintre cei care au făurit America de astăzi.”
          </blockquote>
          <figcaption className="mt-6 eyebrow">— Bill Clinton, 9 iulie 1997</figcaption>
        </figure>
      </section>
    </div>
  );
};

export default Home;
